/**
 * Where to put a floating tooltip.
 *
 * Pure maths, kept out of the component so the awkward cases — an anchor at the
 * edge of the screen, a tooltip taller than the space below it — can be tested
 * without a browser.
 *
 * The reason this exists at all: a tooltip positioned `absolute` inside a
 * scrolling panel is clipped by it. `overflow-y: auto` forces `overflow-x` to
 * `auto` as well, so a tooltip centred on a slot in the panel's leftmost column
 * hangs over the edge and gets cut in half. Tooltips are therefore rendered in a
 * portal, `position: fixed`, with coordinates from here.
 */

/** Space between the anchor and the tooltip. */
export const TOOLTIP_GAP = 8;

/** How close to the viewport edge a tooltip may sit. */
export const VIEWPORT_MARGIN = 8;

export interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface Point {
  left: number;
  top: number;
}

/**
 * Positions a tooltip against its anchor, in viewport coordinates.
 *
 * Centred below by preference. Two things override that, in this order:
 *
 *  - **Horizontal clamping.** A tooltip half off the screen is worse than one
 *    that is not quite centred, so it slides along rather than overhanging.
 *  - **Flipping above.** When there is no room below it goes above; when there
 *    is room in neither direction it sits as low as it can, because a tooltip
 *    pinned off the bottom of the screen is the same bug facing the other way.
 */
export function placeTooltip(
  anchor: Box,
  tip: Size,
  viewport: Size,
  gap: number = TOOLTIP_GAP,
  margin: number = VIEWPORT_MARGIN,
): Point {
  const centred = anchor.left + anchor.width / 2 - tip.width / 2;

  // A tooltip wider than the viewport cannot satisfy both margins; pin it to the
  // left rather than letting the clamp invert.
  const rightmost = Math.max(margin, viewport.width - tip.width - margin);
  const left = Math.min(Math.max(centred, margin), rightmost);

  const below = anchor.top + anchor.height + gap;
  if (below + tip.height + margin <= viewport.height) {
    return { left, top: below };
  }

  const above = anchor.top - tip.height - gap;
  if (above >= margin) {
    return { left, top: above };
  }

  return { left, top: Math.max(margin, viewport.height - tip.height - margin) };
}
